import React from 'react';
import { FaExclamationTriangle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';
import { VoIPAlert } from '../utils/voipTypes';

interface VoIPAlertPanelProps {
  alerts: VoIPAlert[];
  onAlertClick: (alert: VoIPAlert) => void;
}

const VoIPAlertPanel: React.FC<VoIPAlertPanelProps> = ({ alerts, onAlertClick }) => {
  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <FaTimesCircle />;
      case 'warning':
        return <FaExclamationTriangle />;
      default:
        return <FaInfoCircle />;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const criticalAlerts = alerts.filter(a => a.type === 'critical');
  const warningAlerts = alerts.filter(a => a.type === 'warning');

  return (
    <div className="voip-alert-panel">
      <div className="alert-title">
        <FaExclamationTriangle className="alert-icon" />
        Active Alerts
        {alerts.length > 0 && (
          <span className="alert-badge">{alerts.length}</span>
        )}
      </div>

      {alerts.length === 0 ? (
        <div className="no-alerts">
          <FaInfoCircle className="no-alerts-icon" />
          <p>No active alerts</p>
          <span>All VoIP devices are operating normally</span>
        </div>
      ) : (
        <div className="alert-list">
          {criticalAlerts.map((alert) => (
            <div
              key={alert.id}
              className="alert-item critical"
              onClick={() => onAlertClick(alert)}
            >
              <div className="alert-item-icon">
                {getAlertIcon(alert.type)}
              </div>
              <div className="alert-content">
                <h4>{alert.deviceName}</h4>
                <p>{alert.message}</p>
                <div className="alert-meta">
                  <span className="alert-station">{alert.station}</span>
                  <span className="alert-time">{formatTime(alert.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
          {warningAlerts.map((alert) => (
            <div
              key={alert.id}
              className="alert-item warning"
              onClick={() => onAlertClick(alert)}
            >
              <div className="alert-item-icon">
                {getAlertIcon(alert.type)}
              </div>
              <div className="alert-content">
                <h4>{alert.deviceName}</h4>
                <p>{alert.message}</p>
                <div className="alert-meta">
                  <span className="alert-station">{alert.station}</span>
                  <span className="alert-time">{formatTime(alert.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {alerts.length > 0 && (
        <div className="alert-summary">
          <div className="summary-item critical">
            <span className="summary-count">{criticalAlerts.length}</span>
            <span className="summary-label">Critical</span>
          </div>
          <div className="summary-item warning">
            <span className="summary-count">{warningAlerts.length}</span>
            <span className="summary-label">Warning</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoIPAlertPanel;
